import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';
import { fsEvent, getCookie, initializeFullstory } from 'js/fullstory';
import { currentUser } from 'js/utils/user';
import { CitationReport, WorkData } from './types';
import { useCitationReports } from './hooks/useCitationReports';
import useActiveWork from './hooks/useActiveWork';

export interface CiterightContextType {
  reportId: number | null;
  setReportId: (reportId: string | null) => void;
  addClaim: (claim: string) => void;
  startCitationReport: (input?: string | File, rawFile?: File) => Promise<void>;
  report: CitationReport | null;
  error: string | null;
  clearError: () => void;
  clearReportError: () => void;
  uploadError: string | null;
  clearUploadError: () => void;
  activeWorkId: string | null;
  setActiveWorkId: (workId: string | null) => void;
  activeWork: WorkData | null;
  activeClaimId: string | null;
  setActiveClaimId: (claimId: string | null) => void;
  // Saved references functionality
  savedReferences: WorkData[];
  addReferenceToList: (
    reference: WorkData,
    claimId?: string,
    claimText?: string,
  ) => void;
  removeReferenceFromList: (referenceId: string) => void;
  isSaved: (referenceId: string) => boolean;
  // Report-level rerank data availability - calculated once when report is done
  hasAnyRerankData: boolean;
  // Polling status
  isPolling: boolean;
  // Public access
  canEdit: boolean;
  publicToken?: string;
}

export const CiterightContext = createContext<CiterightContextType | null>(
  null,
);

// Helper to read URL param synchronously (SSR safe)
const getUrlParam = (param: string): string | null => {
  if (typeof window === 'undefined') return null;
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(param);
};

export const useUrlParam = (param: string, options?: { replace?: boolean }) => {
  // Initialize with URL value synchronously to avoid flash on first render
  const [value, setValue] = useState<string | null>(() => getUrlParam(param));
  const replace = options?.replace ?? false;

  return {
    value,
    setValue: useCallback(
      (value: string | null) => {
        const urlParams = new URLSearchParams(window.location.search);
        if (value) {
          urlParams.set(param, value);
        } else {
          urlParams.delete(param);
        }
        const queryString = urlParams.toString();
        const newUrl = queryString
          ? window.location.pathname + '?' + queryString
          : window.location.pathname;
        if (replace) {
          window.history.replaceState({}, '', newUrl);
        } else {
          window.history.pushState({}, '', newUrl);
        }
        setValue(value);
      },
      [param, replace],
    ),
  };
};

interface CiterightContextProviderProps {
  children: React.ReactNode;
  publicToken?: string;
  canEdit?: boolean;
}

export const CiterightContextProvider = ({
  children,
  publicToken,
  canEdit = true,
}: CiterightContextProviderProps) => {
  const { value: reportId, setValue: setReportId } = useUrlParam('report_id');
  const [savedReferences, setSavedReferences] = useState<WorkData[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const {
    report,
    error,
    clearError,
    clearReportError,
    startCitationReport,
    startSelectedTextCitationCheck,
    isPolling,
  } = useCitationReports({
    reportId: reportId ? Number(reportId) : null,
    publicToken: publicToken ? publicToken : null,
  });

  const { activeWorkId, setActiveWorkId, activeWork } = useActiveWork(report);
  const [activeClaimId, setActiveClaimId] = useState<string | null>(null);

  const reportReportId = report?.report_id;
  useEffect(() => {
    if (reportReportId) {
      setReportId(reportReportId.toString());
    }
  }, [reportReportId, setReportId, report]);

  // When the user transitions into viewing a report mid-session (e.g. after
  // an upload that doesn't reload the page), tell the backend so it can route
  // this session into the appropriate report.* FullStory flow. The hook is
  // suppressed when a report was already present at mount — those arrivals
  // ran through CiterightController#index, which already called record_flow
  // server-side, so firing again would double-attempt the same visit and
  // skew EvenDistributionStrategy's accept_percentage projection.
  const recordedRef = useRef(
    typeof window !== 'undefined' &&
      (new URLSearchParams(window.location.search).get('report_id') !== null ||
        !!publicToken),
  );
  useEffect(() => {
    if (!reportId || recordedRef.current) return;
    // Cheap optimization: if the landing-flow record_flow on this page load
    // already won its sampling roll, record_session is set and the backend
    // would short-circuit. Skip the round trip.
    if (getCookie('record_session') !== undefined) {
      recordedRef.current = true;
      return;
    }
    recordedRef.current = true;
    fetchWithCsrf(routes.record_report_session_citeright_index_path(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        report_id: reportId,
        public_token: publicToken ?? undefined,
      }),
    })
      .then(() => {
        if (getCookie('record_session') !== undefined) {
          initializeFullstory(currentUser ?? undefined, true);
          fsEvent('citeright_report_engaged');
        }
      })
      .catch(() => {
        // telemetry endpoint, swallow
      });
  }, [reportId, publicToken]);

  // Memoize rerank data availability - calculates as soon as rerank data arrives
  const hasAnyRerankData = useMemo(() => {
    if (!report?.claims) return false;
    return report.claims.some((claim) =>
      claim?.search?.suggested_works?.some(
        (w: WorkData) => w.relevance_score != null,
      ),
    );
  }, [report?.claims]);

  // Saved references functionality
  const addReferenceToList = useCallback(
    (reference: WorkData, claimId?: string, claimText?: string) => {
      setSavedReferences((prev) => {
        // Check if this work is already saved (by work ID)
        const isAlreadySaved = prev.some(
          (ref) => ref.work?.id === reference.work?.id,
        );

        if (isAlreadySaved) {
          return prev;
        }

        // Create a new reference with claim info
        const referenceWithClaim = {
          ...reference,
          claim_id: claimId,
          claim_text: claimText,
        };

        return [...prev, referenceWithClaim];
      });
    },
    [],
  );

  const removeReferenceFromList = useCallback((referenceId: string) => {
    setSavedReferences((prev) =>
      prev.filter((ref) => ref.work?.id !== referenceId),
    );
  }, []);

  const isSaved = useCallback(
    (referenceId: string) => {
      return savedReferences.some((ref) => ref.work?.id === referenceId);
    },
    [savedReferences],
  );

  const clearUploadError = useCallback(() => {
    setUploadError(null);
  }, []);

  return (
    <CiterightContext.Provider
      value={{
        reportId: reportId ? parseInt(reportId) : null,
        setReportId,
        addClaim: startSelectedTextCitationCheck,
        startCitationReport,
        report,
        error,
        clearError,
        clearReportError,
        uploadError,
        clearUploadError,
        activeWorkId,
        setActiveWorkId,
        activeWork,
        activeClaimId,
        setActiveClaimId,
        // Saved references functionality
        savedReferences,
        addReferenceToList,
        removeReferenceFromList,
        isSaved,
        hasAnyRerankData,
        isPolling,
        canEdit,
        publicToken: publicToken ?? undefined,
      }}
    >
      {children}
    </CiterightContext.Provider>
  );
};
